import React from 'react'
import { Bar } from 'react-chartjs-2'
import moment from 'moment'
import ChartCard from '../../../userPages/dashboard/managerOverview/ChartCard'
import {
  baseChartOptions,
  viz,
} from '../../../userPages/dashboard/managerOverview/overviewTheme'

const truncate = (text, max = 26) =>
  text.length > max ? `${text.slice(0, max - 1)}…` : text

// Swaps the base x/y scale styling for horizontal (indexAxis: 'y') bars
const horizontalOptions = {
  ...baseChartOptions,
  indexAxis: 'y',
  scales: {
    x: { ...baseChartOptions.scales.y },
    y: { ...baseChartOptions.scales.x },
  },
}

// Course-by-course mandatory training coverage, weakest courses first
export const CourseComplianceChart = ({ courses = [] }) => {
  const tracked = courses
    .filter((course) => course.totalRequiredEmployees > 0)
    .sort((a, b) => a.compliancePercentage - b.compliancePercentage)
    .slice(0, 8)

  if (tracked.length === 0) return null

  const height = Math.max(160, tracked.length * 34)

  return (
    <ChartCard
      title="Course compliance"
      subtitle="Lowest coverage first"
      height={height}
      columns={['Course', 'Compliant', 'Coverage']}
      rows={tracked.map((course) => [
        course.description,
        `${course.completedEmployees}/${course.totalRequiredEmployees}`,
        `${Math.round(course.compliancePercentage)}%`,
      ])}
    >
      <Bar
        aria-label="Bar chart of mandatory training compliance per course, lowest coverage first"
        data={{
          labels: tracked.map((course) => truncate(course.description)),
          datasets: [
            {
              label: 'Coverage',
              data: tracked.map((course) =>
                Math.round(course.compliancePercentage),
              ),
              backgroundColor: viz.series,
              maxBarThickness: 18,
              borderRadius: { topRight: 4, bottomRight: 4 },
              borderSkipped: 'start',
            },
          ],
        }}
        options={{
          ...horizontalOptions,
          scales: {
            ...horizontalOptions.scales,
            x: {
              ...horizontalOptions.scales.x,
              max: 100,
              ticks: {
                ...horizontalOptions.scales.x.ticks,
                callback: (value) => `${value}%`,
              },
            },
          },
          plugins: {
            ...baseChartOptions.plugins,
            tooltip: {
              ...baseChartOptions.plugins.tooltip,
              callbacks: {
                title: (items) => tracked[items[0].dataIndex]?.description,
                label: (ctx) => {
                  const course = tracked[ctx.dataIndex]
                  return ` ${ctx.raw}% compliant (${course.completedEmployees}/${course.totalRequiredEmployees} staff)`
                },
              },
            },
          },
        }}
      />
    </ChartCard>
  )
}

// Active headcount by clinical role — replaces the old hard-coded doughnut
export const RoleMixChart = ({ headcountByRole = [] }) => {
  const top = headcountByRole.slice(0, 7)
  const otherCount = headcountByRole
    .slice(7)
    .reduce((total, role) => total + role.count, 0)
  const roles = otherCount > 0 ? [...top, { role: 'Other', count: otherCount }] : top

  return (
    <ChartCard
      title="Role mix"
      subtitle="Active staff by clinical role"
      columns={['Role', 'Staff']}
      rows={roles.map((role) => [role.role, role.count])}
    >
      <Bar
        aria-label="Bar chart of active staff headcount by clinical role"
        data={{
          labels: roles.map((role) => truncate(role.role, 20)),
          datasets: [
            {
              label: 'Staff',
              data: roles.map((role) => role.count),
              backgroundColor: viz.series,
              maxBarThickness: 18,
              borderRadius: { topRight: 4, bottomRight: 4 },
              borderSkipped: 'start',
            },
          ],
        }}
        options={{
          ...horizontalOptions,
          plugins: {
            ...baseChartOptions.plugins,
            tooltip: {
              ...baseChartOptions.plugins.tooltip,
              callbacks: {
                title: (items) => roles[items[0].dataIndex]?.role,
                label: (ctx) =>
                  ` ${ctx.raw} staff member${ctx.raw === 1 ? '' : 's'}`,
              },
            },
          },
        }}
      />
    </ChartCard>
  )
}

// New starters joining per month over the last six months
export const StartersTrendChart = ({ startersByMonth = [] }) => (
  <ChartCard
    title="New starters"
    subtitle="Joined in the last 6 months"
    columns={['Month', 'Starters']}
    rows={startersByMonth.map((m) => [
      moment(m.month, 'YYYY-MM').format('MMMM YYYY'),
      m.count,
    ])}
  >
    <Bar
      aria-label="Bar chart of new starters per month over the last six months"
      data={{
        labels: startersByMonth.map((m) =>
          moment(m.month, 'YYYY-MM').format('MMM'),
        ),
        datasets: [
          {
            label: 'Starters',
            data: startersByMonth.map((m) => m.count),
            backgroundColor: viz.series,
            maxBarThickness: 20,
            borderRadius: { topLeft: 4, topRight: 4 },
            borderSkipped: 'bottom',
          },
        ],
      }}
      options={{
        ...baseChartOptions,
        plugins: {
          ...baseChartOptions.plugins,
          tooltip: {
            ...baseChartOptions.plugins.tooltip,
            callbacks: {
              label: (ctx) => ` ${ctx.raw} new starter${ctx.raw === 1 ? '' : 's'}`,
            },
          },
        },
      }}
    />
  </ChartCard>
)
